/**
 * Shared constants + pure helpers for the Chart of Accounts page
 * (Accounts tab, P&L Mapping tab, row components).
 */

export const ACCOUNT_TYPES = ['Asset', 'Liability', 'Equity', 'Revenue', 'Expense', 'Statistics'];
export const DEFAULT_VISIBLE_ACCOUNT_TYPES = ['Revenue', 'Expense', 'Statistics', 'Statistical'];
export const ACCOUNT_GROUPS = [
  'Current Asset',
  'Fixed Asset',
  'Current Liability',
  'Long-Term Liability',
  'Operating Revenue',
  'Non-Operating Revenue',
  'Cost of Goods Sold',
  'Operating Expense',
  'Statistics',
  'Room Statistics',
  'Operating Statistics',
];

export const MATCH_MODES = [
  { key: 'starts', label: 'Starts' },
  { key: 'contains', label: 'Has' },
  { key: 'ends', label: 'Ends' },
];

export const RENDER_CAP = 400;

export const BULK_FIELDS = [
  { key: 'accountType', label: 'Type' },
  { key: 'accountGroup', label: 'Group' },
  { key: 'department', label: 'Department' },
  { key: 'subDepartment', label: 'Sub-department' },
];

// ── P&L mapping vocabulary ─────────────────────────────────────────────────
export const EXPENSE_TYPES = new Set(['Expense', 'Cost of Goods Sold', 'Other Expense']);
export const NON_OPERATING_TYPES = new Set(['Other Income', 'Other Expense']);
// Only P&L accounts participate in the mapping — stat/balance-sheet accounts
// (Statistics, Assets…) never hit the P&L report, so their departments are
// noise there.
export const PNL_TYPES = new Set(['Revenue', 'Cost of Goods Sold', 'Expense', 'Other Income', 'Other Expense']);

// Statement order. The three below-GOP bands close on Income Before Fixed
// Charges, EBITDA and Net Income respectively on the P&L Summary / GOP views.
export const USALI_OPTIONS = [
  { value: 'OPERATED', label: 'Operated' },
  { value: 'UNDISTRIBUTED', label: 'Undistributed' },
  { value: 'MANAGEMENT_FEE', label: 'Management fee' },
  { value: 'NON_OPERATING', label: 'Fixed charges (non-operating)' },
  { value: 'BELOW_EBITDA', label: 'Below EBITDA (interest, D&A, taxes)' },
];
export const USALI_LABELS = new Map(USALI_OPTIONS.map((o) => [o.value, o.label]));
export const usaliLabel = (value) => USALI_LABELS.get(String(value || '').toUpperCase()) || null;

// Mirror of financialPnlMatrix.deptStemKey — role words stripped before
// department names are compared, so the auto hints match the report.
const ROLE_WORDS = new Set(['REV', 'REVENUE', 'REVENUES', 'INCOME', 'INC', 'EXP', 'EXPENSE', 'EXPENSES', 'COST', 'COSTS', 'COS', 'COGS']);
export const stemKey = (d) => String(d || '')
  .toUpperCase()
  .replace(/[^A-Z0-9 &]/g, ' ')
  .split(/\s+/)
  .filter((w) => w && !ROLE_WORDS.has(w))
  .join(' ');

export const acctLabel = (a) => `${a.accountNumber ? `${a.accountNumber} · ` : ''}${a.accountName || `Account ${a.id}`}`;

// ── Generic helpers ────────────────────────────────────────────────────────
export const toOptions = (values) => values.map((v) => ({ value: v, label: v }));
export const optionNameKey = (value) => String(value || '').trim().toLowerCase();
export const normalizeId = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const numeric = Number(value);
  return Number.isNaN(numeric) ? value : numeric;
};
export const subDepartmentParentId = (subDepartment) =>
  normalizeId(subDepartment?.departmentId ?? subDepartment?.chartOfAccountDepartmentId);

/** Union of API-provided names, names already present in the data, and static fallbacks. */
export const unionOptions = (apiNames, dataValues, fallback) => {
  const set = new Set([...(apiNames || []), ...(dataValues || []), ...(fallback || [])].filter(Boolean));
  return toOptions([...set].sort((a, b) => a.localeCompare(b)));
};

export const isDeletedRow = (row) => row?.isDeleted === true || row?.isDeleted === 1;

// ── Manual lock ───────────────────────────────────────────────────────────
// ChartOfAccount.manualFields is a JSON array of the fields a person set by
// hand; the accounting-system sync skips those fields on that row. Editing
// a field here locks it (the server records it); Release hands the row back
// to the sync.
export const MANUAL_FIELD_ORDER = ['accountName', 'accountType', 'accountGroup', 'department', 'subDepartment'];
export const MANUAL_FIELD_LABELS = {
  accountName: 'name',
  accountType: 'type',
  accountGroup: 'group',
  department: 'department',
  subDepartment: 'sub-department',
};

/** Locked field names on a row, in display order ([] when none / unparseable). */
export const manualFieldsOf = (row) => {
  const raw = row?.manualFields;
  if (!raw) return [];
  let list = raw;
  if (typeof raw === 'string') {
    try { list = JSON.parse(raw); } catch { return []; }
  }
  if (!Array.isArray(list)) return [];
  return MANUAL_FIELD_ORDER.filter((f) => list.includes(f));
};

/** Mirror the server's lock bookkeeping locally after an edit (department also locks sub-department). */
export const withManualFields = (row, editedFields) => {
  const next = new Set(manualFieldsOf(row));
  editedFields.forEach((f) => { if (MANUAL_FIELD_ORDER.includes(f)) next.add(f); });
  if (next.has('department')) next.add('subDepartment');
  return { ...row, manualFields: JSON.stringify(MANUAL_FIELD_ORDER.filter((f) => next.has(f))) };
};

export const lockSummary = (row) => {
  const fields = manualFieldsOf(row);
  if (fields.length === 0) return null;
  return `Set by hand: ${fields.map((f) => MANUAL_FIELD_LABELS[f]).join(', ')}. The accounting sync won't overwrite these.`;
};

/** Unmapped = no department. Dashboards drop P&L lines without one. */
export const isUnmapped = (acc) => !acc.department || String(acc.department).trim() === '';

/** Amber when the account still needs a department. */
export const accountStatus = (acc) => {
  if (isDeletedRow(acc)) return { state: 'none', title: 'Deleted — restore to bring it back' };
  if (isUnmapped(acc)) {
    const pnl = acc.accountType === 'Revenue' || acc.accountType === 'Expense';
    return { state: 'partial', title: pnl ? 'No department — excluded from dashboards' : 'No department' };
  }
  return {
    state: 'complete',
    title: `Mapped to ${acc.department}${acc.subDepartment ? ` · ${acc.subDepartment}` : ''}`,
  };
};

// ── P&L department rows (shared by the page flow + the mapping tab) ────────

/** Group revenue-basis links by department id and by account id. */
export const groupLinks = (links) => {
  const byDept = new Map();
  const byAcct = new Map();
  (links || []).forEach((l) => {
    if (l.chartOfAccountDepartmentId != null) {
      if (!byDept.has(l.chartOfAccountDepartmentId)) byDept.set(l.chartOfAccountDepartmentId, []);
      byDept.get(l.chartOfAccountDepartmentId).push(l);
    }
    if (l.chartOfAccountId != null) {
      if (!byAcct.has(l.chartOfAccountId)) byAcct.set(l.chartOfAccountId, []);
      byAcct.get(l.chartOfAccountId).push(l);
    }
  });
  return { byDept, byAcct };
};

/** Revenue departments by stem — powers the "auto-pairs with…" hint and the
 *  inferred Operated section. */
export const revenueDeptStems = (accounts) => {
  const m = new Map();
  (accounts || [])
    .filter((a) => a.accountType === 'Revenue' && !isDeletedRow(a))
    .forEach((a) => {
      const stem = stemKey(a.department);
      if (stem && !m.has(stem)) m.set(stem, String(a.department).trim());
    });
  return m;
};

/** Mirror of the report's section inference. */
export const inferSection = (dept, deptLinks, revenueDeptByStem) => {
  const hasRevenue = dept.accounts.some((a) => a.accountType === 'Revenue')
    || revenueDeptByStem.has(stemKey(dept.name))
    || (deptLinks && deptLinks.length > 0);
  if (hasRevenue) return 'OPERATED';
  const allNonOperating = dept.accounts.length > 0 && dept.accounts.every((a) => NON_OPERATING_TYPES.has(a.accountType));
  return allNonOperating ? 'NON_OPERATING' : 'UNDISTRIBUTED';
};

export const deptRowKey = (dept) => (dept.entity ? `id:${dept.entity.id}` : `name:${dept.name.toLowerCase()}`);

/**
 * Every department that has P&L accounts (income departments need a USALI
 * section too), plus any department that already has links. Accounts attach
 * to their department entity by FK first (so a renamed department keeps its
 * accounts), then by name. Unsorted — the tab orders by displayOrder.
 *
 * Returns [{ key, name, entity, accounts, expenseAccounts, links, section, inferred }].
 */
export const buildDeptRows = (accounts, departments, links) => {
  const { byDept } = groupLinks(links);
  const stems = revenueDeptStems(accounts);
  const deptById = new Map();
  const deptByName = new Map();
  (departments || []).forEach((d) => {
    deptById.set(normalizeId(d.id), d);
    deptByName.set(optionNameKey(d.name), d);
  });
  const rows = new Map();
  (accounts || []).forEach((a) => {
    if (isDeletedRow(a)) return;
    if (!PNL_TYPES.has(a.accountType)) return;
    const name = String(a.department || '').trim();
    const entity = deptById.get(normalizeId(a.chartOfAccountDepartmentId)) || (name ? deptByName.get(name.toLowerCase()) : null) || null;
    if (!entity && !name) return;
    const key = entity ? `id:${entity.id}` : `name:${name.toLowerCase()}`;
    if (!rows.has(key)) rows.set(key, { key, name: entity ? String(entity.name || '').trim() : name, entity, accounts: [] });
    rows.get(key).accounts.push(a);
  });
  (departments || []).forEach((d) => {
    if (!byDept.has(d.id)) return;
    const key = `id:${d.id}`;
    if (!rows.has(key)) rows.set(key, { key, name: String(d.name || '').trim(), entity: d, accounts: [] });
  });
  return [...rows.values()].map((dept) => {
    const deptLinks = dept.entity ? byDept.get(dept.entity.id) || [] : [];
    const section = dept.entity ? (dept.entity.usaliSection || null) : null;
    return {
      ...dept,
      expenseAccounts: dept.accounts.filter((a) => EXPENSE_TYPES.has(a.accountType)),
      links: deptLinks,
      section,
      inferred: inferSection(dept, deptLinks, stems),
      stemMatch: stems.get(stemKey(dept.name)) || null,
    };
  });
};
